import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, MessageCircle, MousePointerClick, Inbox, Inbox as InboxIcon, Eye, Star, LayoutGrid, Video, Award, RefreshCw, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const CARDS = [
  { key: "visitors", label: "Visitors", icon: Users, accent: "text-accent-bright", to: null },
  { key: "page_views", label: "Page Views", icon: Eye, accent: "text-sky-400", to: null },
  { key: "whatsapp_clicks", label: "WhatsApp Clicks", icon: MessageCircle, accent: "text-emerald-400", to: null },
  { key: "button_clicks", label: "Button Clicks", icon: MousePointerClick, accent: "text-amber-400", to: null },
  { key: "total_leads", label: "Total Leads", icon: InboxIcon, accent: "text-white", to: "/admin/leads" },
  { key: "new_leads", label: "New Leads", icon: Inbox, accent: "text-rose-400", to: "/admin/leads" },
  { key: "testimonials", label: "Testimonials", icon: Star, accent: "text-amber-400", to: "/admin/testimonials" },
  { key: "samples", label: "Samples", icon: LayoutGrid, accent: "text-sky-400", to: "/admin/samples" },
  { key: "ugc_models", label: "UGC Models", icon: Video, accent: "text-accent-bright", to: "/admin/ugc" },
  { key: "proofs", label: "Proofs", icon: Award, accent: "text-emerald-400", to: "/admin/proofs" },
];

export default function Dashboard() {
  const { settings } = useSite();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const brand = settings?.brand || {};

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight" data-testid="admin-dashboard-title">
            Welcome back, {(settings?.brand?.short_name || "Scalovix")} Admin
          </h1>
          <p className="font-mono-label text-[10px] text-subtle mt-2">{brand.name || "Scalovix Media Agency"} — Live Overview</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full border border-white/15 text-mist hover:text-white hover:border-accent/50 transition-colors" data-testid="admin-stats-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {CARDS.map((c, i) => (
          <div key={c.key} className="glass rounded-2xl p-5 transition-all duration-300 hover:border-accent/35 hover:-translate-y-0.5" data-testid={`admin-stat-card-${c.key}`}>
            <c.icon className={`h-5 w-5 ${c.accent} mb-3`} />
            <p className="font-display text-2xl sm:text-3xl font-bold text-white" data-testid={`admin-stat-value-${c.key}`}>
              {stats ? (stats[c.key] ?? 0) : "—"}
            </p>
            <p className="font-mono-label text-[9px] text-subtle mt-2">{c.label}</p>
            {c.to && (
              <Link to={c.to} className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-accent-bright hover:gap-2 transition-all" data-testid={`admin-stat-link-${c.key}`}>
                Manage <ArrowUpRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 glass rounded-2xl p-6 sm:p-8">
        <h2 className="font-display text-base font-semibold text-white mb-2">Quick actions</h2>
        <p className="text-xs text-subtle mb-5">Jump straight into the most used managers.</p>
        <div className="flex flex-wrap gap-2.5">
          {[
            { to: "/admin/content", label: "Edit Homepage Content" },
            { to: "/admin/leads", label: "Review Leads" },
            { to: "/admin/samples", label: "Manage Samples" },
            { to: "/admin/settings", label: "Brand & Contact" },
            { to: "/admin/analytics", label: "Tracking Pixels" },
          ].map((q) => (
            <Link key={q.to} to={q.to} className="text-xs font-semibold px-4 py-2.5 rounded-full bg-accent/15 border border-accent/30 text-accent-bright hover:bg-accent hover:text-white transition-all duration-300" data-testid={`admin-quick-action-${q.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {q.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
